/**
 * "Create video" dialog: format and length, a live preview with progress while the cinematic replay records
 * (src/app/cinematic.ts), then Share (Web Share API with the file), Download and Copy caption. No backend.
 */
import { caption, CineFormat, CineLength, videoFileName } from '../game/cinematic';
import type { MessageKey } from '../i18n';
import { h, icon } from './dom';
import { formatTime } from './logic/format';
import type { VideoRequest } from './types';
import { countVideo, shareLine } from './share';
import type { GameUi } from './ui';
import './video.css';

let open: HTMLElement | null = null;

/** The time the caption and the title card show: the best lap, else the route / session total. */
export function resultTime(ui: GameUi): string | null {
  const r = ui.result;
  if (!r || ui.state.screen !== 'results') return null; // a ride in progress (pause menu) has no result yet
  if (r.laps?.length) return formatTime(Math.min(...r.laps));
  return r.total !== undefined ? formatTime(r.total) : null;
}

/** "Create video", or with `share` the primary "Share your ride" (results after a personal best or three stars). */
export function videoButton(ui: GameUi, share = false): HTMLElement | null {
  if (!ui.host?.makeVideo || !ui.host.canMakeVideo?.()) return null;
  return h(`button.btn.video-btn${share ? '.primary.share-btn' : ''}`, { attrs: { 'data-nav': '' }, on: { click: () => openVideoDialog(ui) } },
    [icon('camera'), h('span', { text: ui.tr(share ? 'results.share' : 'video.create') })]);
}

export function openVideoDialog(ui: GameUi): void {
  if (open) return;
  if (ui.replaying) ui.host?.stopReplay();
  const req: VideoRequest = { format: 'landscape', length: 'short' };
  const layer = h('div.mx-ui.cine-layer');
  const close = () => { abort?.abort(); layer.remove(); open = null; if (url) URL.revokeObjectURL(url); };
  let abort: AbortController | null = null, url = '';
  const card = h('div.panel.glass.cine-card', { attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': ui.tr('video.title') } });
  layer.append(h('div.modal.cine-modal', {}, [card]));
  document.body.append(layer);
  open = layer;

  const seg = <T extends string>(label: string, value: () => T, set: (v: T) => void, opts: [T, string][]) => {
    const row = h('div.seg', { attrs: { role: 'radiogroup', 'aria-label': label } });
    const draw = () => row.replaceChildren(...opts.map(([v, text]) => h(`button.seg-btn${v === value() ? '.on' : ''}`, {
      text, attrs: { 'data-nav': '', role: 'radio', 'aria-checked': v === value() }, on: { click: () => { set(v); draw(); } },
    })));
    draw();
    return h('div.cine-opt', {}, [h('h3.section', { text: label }), row]);
  };

  const options = () => {
    card.replaceChildren(
      h('div.eyebrow', {}, [icon('camera'), h('span', { text: ui.tr('video.title') })]),
      h('p.lead', { text: ui.tr('video.lead') }),
      seg<CineFormat>(ui.tr('video.format'), () => req.format, (v) => { req.format = v; }, [['landscape', ui.tr('video.format.landscape')], ['vertical', ui.tr('video.format.vertical')]]),
      seg<CineLength>(ui.tr('video.length'), () => req.length, (v) => { req.length = v; }, [['short', ui.tr('video.length.short')], ['long', ui.tr('video.length.long')], ['full', ui.tr('video.length.full')]]),
      h('p.muted', { text: ui.tr('video.hint') }),
      ...(ui.state.screen === 'pause' ? [h('p.muted.small', { text: ui.tr('video.resumeNote') })] : []),
      h('div.cine-actions', {}, [
        h('button.btn.primary.cine-record', { attrs: { 'data-nav': '' }, on: { click: () => void record() } }, [icon('play'), h('span', { text: ui.tr('video.start') })]),
        h('button.btn', { attrs: { 'data-nav': '' }, on: { click: close } }, [icon('close'), h('span', { text: ui.tr('video.close') })]),
      ]),
    );
  };

  const record = async () => {
    abort = new AbortController();
    const bar = h('i.cine-bar-fill'), label = h('span.cine-pct', { text: ui.tr('video.preparing') });
    const frame = h(`div.cine-preview.${req.format}`);
    card.replaceChildren(
      h('div.eyebrow', {}, [h('i.rec'), h('span', { text: ui.tr('video.title') })]),
      frame,
      h('div.cine-bar', {}, [bar]), label,
      h('div.cine-actions', {}, [h('button.btn', { attrs: { 'data-nav': '' }, on: { click: () => { abort?.abort(); } } }, [icon('close'), h('span', { text: ui.tr('video.cancel') })])]),
    );
    document.body.classList.add('mx-cine');
    try {
      const out = await ui.host!.makeVideo!(req, (p) => {
        bar.style.width = `${Math.round(p.f * 100)}%`;
        label.textContent = p.phase === 'prepare' ? ui.tr('video.preparing') : ui.tr('video.recording', { pct: Math.round(p.f * 100) });
      }, abort.signal, (c) => frame.append(c));
      done(out.blob, out.mime);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') { options(); return; }
      console.warn('[video]', err);
      card.replaceChildren(h('p.lead', { text: ui.tr('video.failed') }), h('div.cine-actions', {}, [h('button.btn.primary', { attrs: { 'data-nav': '' }, on: { click: close } }, [h('span', { text: ui.tr('video.close') })])]));
    } finally {
      document.body.classList.remove('mx-cine');
      abort = null;
    }
  };

  const done = (blob: Blob, mime: string) => {
    url = URL.createObjectURL(blob);
    const track = ui.tr(`level.${ui.host?.loadedLevel ?? 'track'}.name` as MessageKey);
    const name = videoFileName(track, mime);
    const file = new File([blob], name, { type: blob.type });
    const text = caption(shareLine(ui), null);
    countVideo();
    (window as unknown as { __mxVideo?: unknown }).__mxVideo = { blob, mime, name }; // QA (scripts/cine-shot.mjs)
    const canShare = typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
    const copy = async (b: HTMLElement) => {
      try { await navigator.clipboard.writeText(text); } catch { /* blocked: the caption is shown to select */ }
      b.querySelector('span')!.textContent = ui.tr('video.copied');
    };
    const copyBtn: HTMLElement = h('button.btn', { attrs: { 'data-nav': '' }, on: { click: () => void copy(copyBtn) } }, [icon('list'), h('span', { text: ui.tr('video.copyCaption') })]);
    card.replaceChildren(
      h('div.eyebrow', {}, [icon('trophy'), h('span', { text: ui.tr('video.ready') })]),
      h(`video.cine-video.${mime.includes('mp4') ? 'mp4' : 'webm'}`, { attrs: { src: url, controls: true, playsinline: true, loop: true } }),
      h('pre.cine-caption', { text }),
      h('div.cine-actions', {}, [
        canShare ? h('button.btn.primary', { attrs: { 'data-nav': '' }, on: { click: () => void navigator.share({ files: [file], text, title: 'MegaMotocross' }).catch(() => undefined) } }, [icon('chat'), h('span', { text: ui.tr('video.share') })]) : null,
        h(`a.btn${canShare ? '' : '.primary'}`, { attrs: { href: url, download: name, 'data-nav': '' } }, [icon('next'), h('span', { text: ui.tr('video.download') })]),
        copyBtn,
        h('button.btn', { attrs: { 'data-nav': '' }, on: { click: close } }, [icon('close'), h('span', { text: ui.tr('video.close') })]),
      ]),
    );
  };

  options();
}
